import React, { createContext, useContext, useState } from 'react';
import { products } from '../data/products';

// Team member mapping
const teamMembers = {
  1: { id: 1, name: 'Admin', role: 'admin', canViewAll: true },
  257: { id: 257, name: 'Sara Ahmed', role: 'team_member', canViewAll: false },
  289: { id: 289, name: 'Ali Hassan', role: 'team_member', canViewAll: false },
  306: { id: 306, name: 'Fatima Khan', role: 'team_member', canViewAll: false }
};

const OrderContext = createContext();

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

export const OrderProvider = ({ children }) => {
  // Order status system with 9 checkpoints
  const orderStatusSystem = {
    'Order Placed': { icon: 'fas fa-check-circle', color: '#10b981', step: 1 },
    'Order Confirmed': { icon: 'fas fa-clipboard-check', color: '#10b981', step: 2 },
    'Processing': { icon: 'fas fa-cogs', color: '#3b82f6', step: 3 },
    'Packed': { icon: 'fas fa-box', color: '#3b82f6', step: 4 },
    'Dispatched': { icon: 'fas fa-shipping-fast', color: '#f59e0b', step: 5 },
    'In Transit': { icon: 'fas fa-truck', color: '#f59e0b', step: 6 },
    'Out for Delivery': { icon: 'fas fa-route', color: '#f59e0b', step: 7 },
    'Delivered': { icon: 'fas fa-home', color: '#10b981', step: 8 },
    'Cancelled': { icon: 'fas fa-times-circle', color: '#ef4444', step: 0 }
  };

  const orderStatusOptions = [
    'Order Placed',
    'Order Confirmed', 
    'Processing',
    'Packed',
    'Dispatched',
    'In Transit',
    'Out for Delivery',
    'Delivered',
    'Cancelled'
  ];

  // Enhanced orders state with real user data and additional orders
  const [orders, setOrders] = useState([
    {
      id: '#SW150124W001',
      orderNumber: 'SW150124W001',
      customer: 'Ahmed Khan',
      email: 'ahmed.khan@email.com',
      phone: '+92 300 1234567',
      address: 'Lahore, Pakistan',
      userId: 2, // Linked to user ID
      date: '2024-01-15',
      deliveredDate: '2024-01-20',
      status: 'Delivered',
      trackingSteps: ['Order Placed', 'Order Confirmed', 'Processing', 'Packed', 'Dispatched', 'In Transit', 'Out for Delivery', 'Delivered'],
      currentStep: 8,
      total: 4990,
      itemCount: 1,
      items: [
        {
          productId: products[0].id,
          name: products[0].name,
          size: 'M',
          color: products[0].colors[0],
          quantity: 1,
          price: products[0].price,
          image: products[0].image,
          url: products[0].url,
          createdBy: products[0].createdBy, // Team member who created this product
          teamMemberName: teamMembers[products[0].createdBy]?.name || 'Unknown'
        }
      ],
      paymentMethod: 'Credit Card'
    },
    {
      id: '#SW100124W002',
      orderNumber: 'SW100124W002',
      customer: 'Fatima Ali',
      email: 'fatima.ali@email.com',
      phone: '+92 321 9876543',
      address: 'Karachi, Pakistan',
      userId: 3,
      date: '2024-01-10',
      status: 'In Transit',
      trackingSteps: ['Order Placed', 'Order Confirmed', 'Processing', 'Packed', 'Dispatched', 'In Transit'],
      currentStep: 6,
      total: 8990,
      itemCount: 1,
      items: [
        {
          productId: products[2].id,
          name: products[2].name,
          size: 'M',
          color: products[2].colors[0],
          quantity: 1,
          price: products[2].price,
          image: products[2].image,
          url: products[2].url,
          createdBy: products[2].createdBy,
          teamMemberName: teamMembers[products[2].createdBy]?.name || 'Unknown'
        }
      ],
      paymentMethod: 'JazzCash'
    },
    {
      id: '#SW080124A003',
      orderNumber: 'SW080124A003',
      customer: 'Hassan Ali',
      email: '123@email.com',
      phone: '+92 300 5555555',
      address: 'Islamabad, Pakistan',
      userId: 4,
      date: '2024-01-08',
      status: 'Processing',
      trackingSteps: ['Order Placed', 'Order Confirmed', 'Processing'],
      currentStep: 3,
      total: 6990,
      itemCount: 1,
      items: [
        {
          productId: products[7].id,
          name: products[7].name,
          size: 'L',
          color: products[7].colors[0],
          quantity: 1,
          price: products[7].price,
          image: products[7].image,
          url: products[7].url,
          createdBy: products[7].createdBy,
          teamMemberName: teamMembers[products[7].createdBy]?.name || 'Unknown'
        }
      ],
      paymentMethod: 'Cash on Delivery'
    },
    {
      id: '#SW050124F004',
      orderNumber: 'SW050124F004',
      customer: 'Aisha Khan',
      email: 'aisha.khan@email.com',
      phone: '+92 345 1111111',
      address: 'Faisalabad, Pakistan',
      userId: 5,
      date: '2024-01-05',
      status: 'Out for Delivery',
      trackingSteps: ['Order Placed', 'Order Confirmed', 'Processing', 'Packed', 'Dispatched', 'In Transit', 'Out for Delivery'],
      currentStep: 7,
      total: 2990,
      itemCount: 1,
      items: [
        {
          productId: products[5].id,
          name: products[5].name,
          size: 'L',
          color: products[5].colors[0],
          quantity: 1,
          price: products[5].price,
          image: products[5].image,
          url: products[5].url,
          createdBy: products[5].createdBy,
          teamMemberName: teamMembers[products[5].createdBy]?.name || 'Unknown'
        }
      ],
      paymentMethod: 'JazzCash'
    },
    {
      id: '#SW120124W005',
      orderNumber: 'SW120124W005',
      customer: 'Sara Ahmed',
      email: 'sara.ahmed@email.com',
      phone: '+92 333 2222222',
      address: 'Multan, Pakistan',
      userId: 6,
      date: '2024-01-12',
      deliveredDate: '2024-01-18',
      status: 'Delivered',
      trackingSteps: ['Order Placed', 'Order Confirmed', 'Processing', 'Packed', 'Dispatched', 'In Transit', 'Out for Delivery', 'Delivered'],
      currentStep: 8,
      total: 4490,
      itemCount: 1,
      items: [
        {
          productId: products[3].id,
          name: products[3].name,
          size: 'M',
          color: products[3].colors[0],
          quantity: 1,
          price: products[3].price,
          image: products[3].image,
          url: products[3].url,
          createdBy: products[3].createdBy,
          teamMemberName: teamMembers[products[3].createdBy]?.name || 'Unknown'
        }
      ],
      paymentMethod: 'Debit Card'
    },
    {
      id: '#SW140124W006',
      orderNumber: 'SW140124W006',
      customer: 'Ali Hassan',
      email: 'ali.hassan@email.com',
      phone: '+92 334 7777777',
      address: 'Rawalpindi, Pakistan',
      userId: 7,
      date: '2024-01-14',
      status: 'Order Confirmed',
      trackingSteps: ['Order Placed', 'Order Confirmed'],
      currentStep: 2,
      total: 7890,
      itemCount: 2,
      items: [
        {
          productId: products[1].id,
          name: products[1].name,
          size: 'L',
          color: products[1].colors[0],
          quantity: 1,
          price: products[1].price,
          image: products[1].image,
          url: products[1].url,
          createdBy: products[1].createdBy,
          teamMemberName: teamMembers[products[1].createdBy]?.name || 'Unknown'
        },
        {
          productId: products[4].id,
          name: products[4].name,
          size: 'M',
          color: products[4].colors[0],
          quantity: 1,
          price: products[4].price,
          image: products[4].image,
          url: products[4].url,
          createdBy: products[4].createdBy,
          teamMemberName: teamMembers[products[4].createdBy]?.name || 'Unknown'
        }
      ],
      paymentMethod: 'Bank Transfer'
    },
    {
      id: '#SW120124S007',
      orderNumber: 'SW120124S007',
      customer: 'Ali Hassan',
      email: 'ali.hassan@email.com',
      phone: '+92 334 7777777',
      address: 'Rawalpindi, Pakistan',
      userId: 7,
      date: '2024-01-12',
      deliveredDate: '2024-01-17',
      status: 'Delivered',
      trackingSteps: ['Order Placed', 'Order Confirmed', 'Processing', 'Packed', 'Dispatched', 'In Transit', 'Out for Delivery', 'Delivered'],
      currentStep: 8,
      total: 3990,
      itemCount: 1,
      items: [
        {
          productId: products[6].id,
          name: products[6].name,
          size: 'L',
          color: products[6].colors[0],
          quantity: 1,
          price: products[6].price,
          image: products[6].image,
          url: products[6].url,
          createdBy: products[6].createdBy,
          teamMemberName: teamMembers[products[6].createdBy]?.name || 'Unknown'
        }
      ],
      paymentMethod: 'EasyPaisa'
    },
    {
      id: '#SW060124S008',
      orderNumber: 'SW060124S008',
      customer: 'Usman Ali',
      email: 'usman.ali@email.com',
      phone: '+92 303 0000000',
      address: 'Quetta, Pakistan',
      userId: 10,
      date: '2024-01-06',
      status: 'Cancelled',
      trackingSteps: ['Order Placed', 'Cancelled'],
      currentStep: 0,
      total: 2990,
      itemCount: 1,
      items: [
        {
          productId: products[8].id,
          name: products[8].name,
          size: 'M',
          color: products[8].colors[0],
          quantity: 1,
          price: products[8].price,
          image: products[8].image,
          url: products[8].url,
          createdBy: products[8].createdBy,
          teamMemberName: teamMembers[products[8].createdBy]?.name || 'Unknown'
        }
      ],
      paymentMethod: 'Cash on Delivery'
    }
  ]);

  // Order status update function - Shared between admin and customer views
  const updateOrderStatus = (orderId, newStatus) => {
    setOrders(prevOrders => 
      prevOrders.map(order => {
        if (order.id === orderId) {
          // Update tracking steps based on new status
          const statusIndex = orderStatusOptions.indexOf(newStatus);
          const newTrackingSteps = orderStatusOptions.slice(0, statusIndex + 1);
          
          const updatedOrder = {
            ...order,
            status: newStatus,
            trackingSteps: newTrackingSteps,
            currentStep: statusIndex + 1
          };

          // Add delivered date if status is delivered
          if (newStatus === 'Delivered') {
            updatedOrder.deliveredDate = new Date().toISOString().split('T')[0];
          }

          return updatedOrder;
        }
        return order;
      })
    );
  };

  // Generate new order number with date and category
  const generateOrderNumber = (items) => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = String(today.getFullYear()).slice(-2);
    const date = `${day}${month}${year}`; // DDMMYY format
    
    // Get category code from first item
    let categoryCode = 'G'; // General default
    if (items && items.length > 0) {
      const firstItem = items[0];
      const product = products.find(p => p.id === firstItem.productId);
      if (product) {
        const categoryCodes = {
          'women': 'W',
          'men': 'M', 
          'kids': 'K',
          'accessories': 'A',
          'fragrances': 'F',
          'special': 'S'
        };
        categoryCode = categoryCodes[product.mainCategory] || 'G';
      }
    }
    
    // Get sequential number (overall orders count)
    const sequentialNumber = String(orders.length + 1).padStart(3, '0');
    
    return `SW${date}${categoryCode}${sequentialNumber}`;
  };

  // Add new order function
  const addOrder = (orderData) => {
    const orderNumber = generateOrderNumber(orderData.items);
    const newOrder = {
      ...orderData,
      id: `#${orderNumber}`,
      orderNumber: orderNumber,
      date: new Date().toISOString().split('T')[0],
      status: 'Order Placed',
      trackingSteps: ['Order Placed'],
      currentStep: 1,
      itemCount: orderData.items ? orderData.items.length : 0
    };
    setOrders(prevOrders => [...prevOrders, newOrder]);
    return orderNumber;
  };

  // Get orders by customer email
  const getOrdersByCustomer = (email) => {
    return orders.filter(order => order.email === email);
  };

  // Get orders by user ID
  const getOrdersByUserId = (userId) => {
    return orders.filter(order => order.userId === userId);
  };

  // Get order statistics for a user
  const getUserOrderStats = (userId) => {
    const userOrders = getOrdersByUserId(userId);
    const totalOrders = userOrders.length;
    const totalSpent = userOrders.reduce((sum, order) => sum + order.total, 0);
    const deliveredOrders = userOrders.filter(order => order.status === 'Delivered').length;
    const cancelledOrders = userOrders.filter(order => order.status === 'Cancelled').length;
    
    return {
      totalOrders,
      totalSpent,
      deliveredOrders,
      cancelledOrders,
      averageOrderValue: totalOrders > 0 ? Math.round(totalSpent / totalOrders) : 0
    };
  };

  // Link order to user (useful when creating new orders)
  const linkOrderToUser = (orderData, userId) => {
    const orderNumber = generateOrderNumber(orderData.items);
    return {
      ...orderData,
      userId: userId,
      id: `#${orderNumber}`,
      orderNumber: orderNumber
    };
  };

  // Get all orders with enhanced customer info
  const getOrdersWithCustomerInfo = () => {
    return orders.map(order => ({
      ...order,
      customerType: order.userId === 1 ? 'Admin' : 'Customer',
      isVIP: order.total > 10000,
      orderCount: orders.filter(o => o.userId === order.userId).length
    }));
  };

  // Get orders by team member ID (admin can see all, team members see only their products)
  const getOrdersByTeamMember = (teamMemberId) => {
    if (!teamMemberId) return orders;
    
    const teamMember = teamMembers[teamMemberId];
    if (!teamMember) return [];
    
    // Admin can see all orders
    if (teamMember.canViewAll) {
      return orders;
    }
    
    // Team members can only see orders containing their products
    return orders.filter(order => {
      return order.items.some(item => item.createdBy === teamMemberId);
    });
  };

  // Get products that can be edited by team member
  const getEditableProductsByTeamMember = (teamMemberId) => {
    if (!teamMemberId) return [];
    
    const teamMember = teamMembers[teamMemberId];
    if (!teamMember) return [];
    
    // Admin can edit all products
    if (teamMember.canViewAll) {
      return products;
    }
    
    // Team members can only edit their own products
    return products.filter(product => product.createdBy === teamMemberId);
  };

  // Check if team member can edit specific product
  const canEditProduct = (teamMemberId, productId) => {
    if (!teamMemberId || !productId) return false;
    
    const teamMember = teamMembers[teamMemberId];
    if (!teamMember) return false;
    
    // Admin can edit all products
    if (teamMember.canViewAll) return true;
    
    // Find the product and check if team member created it
    const product = products.find(p => p.id === productId);
    return product && product.createdBy === teamMemberId;
  };

  // Update addOrder function to include team member info for new orders
  const addOrderWithTeamMemberInfo = (orderData) => {
    const orderNumber = generateOrderNumber(orderData.items);
    
    // Add team member info to each item
    const itemsWithTeamInfo = orderData.items?.map(item => {
      const product = products.find(p => p.id === item.productId);
      return {
        ...item,
        createdBy: product?.createdBy,
        teamMemberName: product?.createdBy ? teamMembers[product.createdBy]?.name || 'Unknown' : 'Unknown'
      };
    }) || [];
    
    const newOrder = {
      ...orderData,
      id: `#${orderNumber}`,
      orderNumber: orderNumber,
      date: new Date().toISOString().split('T')[0],
      status: 'Order Placed',
      trackingSteps: ['Order Placed'],
      currentStep: 1,
      itemCount: itemsWithTeamInfo.length,
      items: itemsWithTeamInfo
    };
    
    setOrders(prevOrders => [...prevOrders, newOrder]);
    return orderNumber;
  };

  const value = {
    orders,
    setOrders,
    orderStatusSystem,
    orderStatusOptions,
    updateOrderStatus,
    addOrder,
    addOrderWithTeamMemberInfo,
    getOrdersByCustomer,
    getOrdersByUserId,
    getUserOrderStats,
    linkOrderToUser,
    getOrdersWithCustomerInfo,
    getOrdersByTeamMember,
    getEditableProductsByTeamMember,
    canEditProduct,
    teamMembers
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
}; 